import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FolderOpen, Folder, FileBox, ChevronRight, ChevronDown } from 'lucide-react';
import { VAULT_DATA } from '@/components/vaultData';

export default function FolderTree() {
  const [expandedSet, setExpandedSet] = useState(new Set());

  const toggle = useCallback((name) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const rows = [];
  VAULT_DATA.children.forEach((folder, i) => {
    rows.push({ ...folder, depth: 0, index: i });
    if (expandedSet.has(folder.name) && folder.children) {
      folder.children.forEach((child, j) => {
        rows.push({ ...child, depth: 1, index: j });
      });
    }
  });

  return (
    <div className="max-h-[420px] overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#ddd transparent' }}>
      {rows.map((item) => {
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedSet.has(item.name);
        const pl = item.depth * 20 + 12;

        return (
          <motion.div
            key={`${item.depth}-${item.name}`}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: item.index * 0.03, duration: 0.25 }}
          >
            <button
              onClick={() => hasChildren && toggle(item.name)}
              className="w-full flex items-center gap-3 py-2.5 px-3 transition-colors duration-200 group text-left"
              style={{
                paddingLeft: `${pl}px`,
                background: isExpanded ? 'rgba(253,195,161,0.08)' : '#FFFFFF',
                borderBottom: '1px solid rgba(0,0,0,0.04)',
              }}
              data-testid={`vault-folder-${item.name.toLowerCase().replace(/[\s&]+/g, '-')}`}
            >
              <span className="w-4 h-4 flex items-center justify-center text-[#bbb]">
                {hasChildren ? (
                  isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />
                ) : (
                  <span className="w-3.5" />
                )}
              </span>

              {isExpanded ? (
                <FolderOpen className="w-4 h-4 text-[#FDC3A1] shrink-0" />
              ) : (
                <Folder className="w-4 h-4 text-[#ddb89a] group-hover:text-[#FDC3A1] shrink-0 transition-colors" />
              )}

              <span className="font-body text-sm text-[#333] group-hover:text-[#111] transition-colors truncate">
                {item.name}
              </span>

              <span className="flex-1" />

              <span className="flex items-center gap-1.5 shrink-0">
                <FileBox className="w-3 h-3 text-[#ccc]" />
                <span className="font-mono text-xs text-[#999] tabular-nums">
                  {item.files.toLocaleString()} files
                </span>
              </span>

              <span className="font-mono text-xs text-[#bbb] tabular-nums w-20 text-right shrink-0">
                {item.size}
              </span>
            </button>
          </motion.div>
        );
      })}
    </div>
  );
}
